import PropTypes from "prop-types";
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  FilledInput,
  FormControl
} from "@mui/material";
import { useTranslation } from "react-i18next";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import { useDispatch, useSelector } from "react-redux";
import { fetchShortCoursePageTemplates } from "../../redux/actions/courseBuilderActions";
import { useTheme } from "@mui/material/styles";
import { insertCustomerStyles } from "../../utils/htmlEditorUtils";

const NewPageFieldContent = ({
  isSubmitting,
  content,
  templateOptions,
  filesList,
  setFilesList,
  setFieldValue,
  onContentChange
}) => {
  const { t } = useTranslation();
  const [currentTemplate, setCurrentTemplate] = useState("");

  const handleTemplateChange = newTemplate => {
    setCurrentTemplate(newTemplate);
    onContentChange(newTemplate.content);
    setFieldValue("content", newTemplate.content);
  };

  return (
    <Box display="flex" flexDirection="column" mb={1}>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        mb={1.5}
        mt={1}
      >
        <Box flex={1}>
          <FormControl variant="filled" fullWidth>
            <InputLabel htmlFor="new-page-template-label">
              {t("courseBuilder.createNewPageModalTemplateDropdown")}
            </InputLabel>
            <Select
              input={
                <FilledInput
                  id="new-page-template-label"
                  value={currentTemplate || ""}
                />
              }
              onChange={ev => {
                handleTemplateChange(ev.target.value);
              }}
              disableUnderline
              renderValue={value => value.title}
            >
              {templateOptions &&
                templateOptions.map(option => (
                  <MenuItem value={option} key={"template_" + option.id}>
                    {t(option.title)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
      <HTMLEditor
        disabled={isSubmitting}
        height={300}
        onChange={value => {
          setFieldValue("content", value);
          onContentChange(value);
        }}
        value={content}
        onFileAdd={(fileObj, tempLink, type) => {
          let list = filesList;
          list.push({ fileObj, tempLink, type });
          setFilesList(list);
        }}
        menubar={false}
        plugins={[
          "lists",
          "link",
          "image",
          "media",
          "code",
          "advlist",
          "fileUpload",
          "textcolor",
          "fullpage"
        ]}
        toolbar="link image media code | styleselect bold italic underline | outdent indent bullist | fontselect fontsizeselect forecolor"
      />
    </Box>
  );
};

const NewPageField = ({
  filesList,
  setFilesList,
  setFieldValue,
  isSubmitting,
  onContentChange,
  content
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const shortCoursePageTemplates = useSelector(
    ({ courseBuilderState }) => courseBuilderState.shortCoursePageTemplates
  );

  useEffect(() => {
    if (!shortCoursePageTemplates) {
      dispatch(fetchShortCoursePageTemplates());
    }
  }, [shortCoursePageTemplates]);

  const templatesWithStyles = useMemo(() => {
    if (shortCoursePageTemplates) {
      return shortCoursePageTemplates.map(template => ({
        ...template,
        content: insertCustomerStyles(template.content, theme)
      }));
    } else {
      return [];
    }
  }, [shortCoursePageTemplates]);

  return (
    <NewPageFieldContent
      filesList={filesList}
      setFilesList={setFilesList}
      setFieldValue={setFieldValue}
      templateOptions={templatesWithStyles}
      onContentChange={onContentChange}
      content={content}
      isSubmitting={isSubmitting}
    />
  );
};

NewPageField.propTypes = {
  filesList: PropTypes.array,
  setFilesList: PropTypes.func,
  setFieldValue: PropTypes.func,
  templateOptions: PropTypes.array,
  onContentChange: PropTypes.func,
  content: PropTypes.string,
  isSubmitting: PropTypes.bool
};

export default NewPageField;
